import { useGuestSiteTelemetry } from '@apps/guest/GuestSite.telemetry';
import { EventPageFragment } from '@graphql/generated';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useEventUserRole } from '@shared/components/AuthProvider';
import { GuestSiteQueryParams, useGuestSiteQueryParams } from './useGuestSiteQueryParams';
import { getPageInfo } from './useGuestSitePageChanged';
import { PageAction } from '@shared/core/analytics/context';
import { debounce } from 'lodash-es';

type PageTelemetry = {
  [key in PageAction]: (role: string, args: GuestSiteQueryParams, pageName?: string) => void;
};

const TEN_SECOND_TIMEOUT = 10000;
const TEN_SECOND_PREFIX = '10';
const THIRTY_SECOND_TIMEOUT = 30000;
const THIRTY_SECOND_PREFIX = '30';

export const useGuestSitePageViewedDuration = (pages: ReadonlyArray<EventPageFragment>, isMigratedCustomPageEnabled?: (slug: string) => boolean) => {
  const telemetry = useGuestSiteTelemetry();
  const [currentPageSlug, setCurrentPageSlug] = useState<string>();
  const { isAdmin } = useEventUserRole();
  const queryParams = useGuestSiteQueryParams();

  // Track dwell time events that have been fired to avoid duplicates
  const firedEvents = useRef<Set<string>>(new Set());
  const debouncedEvents = useRef<{
    tenSecond?: ReturnType<typeof debounce>;
    thirtySecond?: ReturnType<typeof debounce>;
  }>({});

  const clearDebouncedEvents = useCallback(() => {
    if (debouncedEvents.current.tenSecond) {
      debouncedEvents.current.tenSecond.cancel();
      debouncedEvents.current.tenSecond = undefined;
    }
    if (debouncedEvents.current.thirtySecond) {
      debouncedEvents.current.thirtySecond.cancel();
      debouncedEvents.current.thirtySecond = undefined;
    }
  }, []);

  const firePageEvent = useCallback(
    (pageSlug: string, pageFound: EventPageFragment, pageTelemetry: PageTelemetry, duration: string) => {
      const eventKey = `${pageSlug}-${isAdmin ? 'admin' : 'guest'}-${duration}s`;
      if (!firedEvents.current.has(eventKey)) {
        pageTelemetry.enter(
          isAdmin ? 'admin' : 'guest',
          {
            ...queryParams,
            slug: pageFound.pageSlug,
          },
          duration,
        );
        firedEvents.current.add(eventKey);
      }
    },
    [isAdmin, queryParams],
  );

  const trackPageVisitDuration = useCallback(
    locationPathName => {
      const { pageSlug, pageFound } = getPageInfo(pages, locationPathName, { isMigratedCustomPageEnabled });
      const pageTelemetry = telemetry[pageSlug as keyof typeof telemetry] as PageTelemetry;
      if (pageFound && pageSlug && pageTelemetry && currentPageSlug !== pageFound.pageSlug) {
        // Clear any existing debounced events when page changes
        clearDebouncedEvents();
        firedEvents.current.clear();

        setCurrentPageSlug(pageFound.pageSlug);

        debouncedEvents.current.tenSecond = debounce(() => firePageEvent(pageSlug, pageFound, pageTelemetry, TEN_SECOND_PREFIX), TEN_SECOND_TIMEOUT);
        debouncedEvents.current.thirtySecond = debounce(() => firePageEvent(pageSlug, pageFound, pageTelemetry, THIRTY_SECOND_PREFIX), THIRTY_SECOND_TIMEOUT);

        // Trigger the debounced events
        debouncedEvents.current.tenSecond();
        debouncedEvents.current.thirtySecond();
      }
    },
    [pages, telemetry, currentPageSlug, clearDebouncedEvents, firePageEvent, isMigratedCustomPageEnabled],
  );

  // Cleanup debounced events on unmount
  useEffect(() => {
    return () => {
      clearDebouncedEvents();
    };
  }, [clearDebouncedEvents]);

  return {
    trackPageVisitDuration,
  };
};
